import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  Avatar,
  IconButton,
  TextField,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  FlightTakeoff,
  Add,
  Edit,
  Delete,
  Assessment,
  FilterList,
  CalendarMonth,
  AccessTime,
  Place,
  Air,
  Person,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface FlightLog {
  id: string;
  pilot_id: string;
  flight_date: string;
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
  wing?: string;
  wing_size?: string;
  takeoff_site?: string;
  landing_site?: string;
  flight_type: 'solo' | 'tandem';
  passenger_name?: string;
  passenger_weight?: number;
  notes?: string;
  weather_conditions?: string;
  wind_direction?: string;
  wind_speed?: string;
  max_altitude?: number;
  distance_km?: number;
  created_at: string;
  updated_at: string;
}

interface FlightStats {
  total_flights: number;
  total_flight_time_minutes: number;
  total_flight_time_hours: number;
  solo_flights: number;
  tandem_flights: number;
  flights_this_year: number;
  flights_this_month: number;
  average_flight_duration_minutes: number;
  longest_flight_minutes?: number;
  max_altitude_ever?: number;
  total_distance_km?: number;
  most_used_wing?: string;
  favorite_takeoff_site?: string;
}

const LogbookDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [loading, setLoading] = useState(true);
  const [flights, setFlights] = useState<FlightLog[]>([]);
  const [stats, setStats] = useState<FlightStats | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [yearFilter, setYearFilter] = useState<number | ''>('');
  const [monthFilter, setMonthFilter] = useState<number | ''>('');
  const [flightTypeFilter, setFlightTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flightToDelete, setFlightToDelete] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const fetchFlights = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, page_size: 10 };
      if (yearFilter) params.year = yearFilter;
      if (monthFilter) params.month = monthFilter;
      if (flightTypeFilter) params.flight_type = flightTypeFilter;

      const response = await api.get('/logbook/flights', { params });
      setFlights(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Failed to fetch flights:', err);
    } finally {
      setLoading(false);
    }
  }, [page, yearFilter, monthFilter, flightTypeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/logbook/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.is_pilot || currentUser?.user_type === 'pilot') {
      fetchFlights();
      fetchStats();
    }
  }, [currentUser, fetchFlights, fetchStats]);

  const handleDeleteClick = (flightId: string) => {
    setFlightToDelete(flightId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!flightToDelete) return;
    try {
      await api.delete(`/logbook/flights/${flightToDelete}`);
      setDeleteDialogOpen(false);
      setFlightToDelete(null);
      fetchFlights();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete flight:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const renderStatsCard = () => {
    if (!stats) return null;

    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment color="primary" />
          {t('logbook.stats.title')}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
              {stats.total_flights}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('logbook.stats.totalFlights')}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 120 }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
              {stats.total_flight_time_hours}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('logbook.stats.totalHours')}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 120 }}>
            <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
              {stats.flights_this_year}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('logbook.stats.thisYear')}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 120 }}>
            <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
              {stats.flights_this_month}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('logbook.stats.thisMonth')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3 }}>
          <Chip
            label={`${t('logbook.stats.solo')}: ${stats.solo_flights}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${t('logbook.stats.tandem')}: ${stats.tandem_flights}`}
            color="secondary"
            variant="outlined"
          />
          {stats.average_flight_duration_minutes > 0 && (
            <Chip
              label={`${t('logbook.stats.avgDuration')}: ${formatDuration(Math.round(stats.average_flight_duration_minutes))}`}
              variant="outlined"
            />
          )}
          {stats.most_used_wing && (
            <Chip
              icon={<Air />}
              label={stats.most_used_wing}
              variant="outlined"
            />
          )}
          {stats.favorite_takeoff_site && (
            <Chip
              icon={<Place />}
              label={stats.favorite_takeoff_site}
              variant="outlined"
            />
          )}
        </Box>
      </Paper>
    );
  };

  const renderFlightCard = (flight: FlightLog) => (
    <Card key={flight.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: flight.flight_type === 'tandem' ? 'secondary.main' : 'primary.main' }}>
                <FlightTakeoff />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {formatDate(flight.flight_date)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    size="small"
                    label={flight.flight_type === 'tandem' ? t('logbook.tandem') : t('logbook.solo')}
                    color={flight.flight_type === 'tandem' ? 'secondary' : 'primary'}
                  />
                  {flight.duration_minutes && (
                    <Chip
                      size="small"
                      icon={<AccessTime />}
                      label={formatDuration(flight.duration_minutes)}
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {(flight.start_time || flight.end_time) && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.time')}
                  </Typography>
                  <Typography variant="body1">
                    {formatTime(flight.start_time)} - {formatTime(flight.end_time)}
                  </Typography>
                </Box>
              )}

              {flight.wing && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.wing')}
                  </Typography>
                  <Typography variant="body1">
                    {flight.wing} {flight.wing_size && `(${flight.wing_size})`}
                  </Typography>
                </Box>
              )}

              {flight.takeoff_site && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.takeoffSite')}
                  </Typography>
                  <Typography variant="body1">{flight.takeoff_site}</Typography>
                </Box>
              )}

              {flight.landing_site && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.landingSite')}
                  </Typography>
                  <Typography variant="body1">{flight.landing_site}</Typography>
                </Box>
              )}

              {flight.flight_type === 'tandem' && flight.passenger_name && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.passenger')}
                  </Typography>
                  <Typography variant="body1">
                    <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {flight.passenger_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {flight.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('logbook.notes')}
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {flight.notes}
                </Typography>
              </Box>
            )}

            {(flight.weather_conditions || flight.wind_direction || flight.wind_speed) && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {flight.weather_conditions && (
                  <Chip size="small" label={flight.weather_conditions} variant="outlined" />
                )}
                {flight.wind_direction && (
                  <Chip size="small" label={`${t('logbook.wind')}: ${flight.wind_direction}`} variant="outlined" />
                )}
                {flight.wind_speed && (
                  <Chip size="small" label={flight.wind_speed} variant="outlined" />
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Tooltip title={t('logbook.edit')}>
              <IconButton
                size="small"
                onClick={() => navigate(`/app/logbook/edit/${flight.id}`)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('logbook.delete')}>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(flight.id)}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && flights.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 80, height: 80 }}>
              <FlightTakeoff sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                {t('logbook.title')}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {t('logbook.subtitle')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/app/logbook/add')}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            {t('logbook.addFlight')}
          </Button>
        </Box>
      </Paper>

      {/* Stats */}
      {renderStatsCard()}

      {/* Flights List */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" />
            {t('logbook.flightHistory')} ({total})
          </Typography>
          <Button
            startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowFilters(!showFilters)}
            endIcon={<FilterList />}
          >
            {t('logbook.filters')}
          </Button>
        </Box>

        {/* Filters */}
        {showFilters && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={t('logbook.year')}
              value={yearFilter}
              onChange={(e) => {
                setYearFilter(e.target.value ? Number(e.target.value) : '');
                setPage(1);
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{t('logbook.all')}</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('logbook.month')}
              value={monthFilter}
              onChange={(e) => {
                setMonthFilter(e.target.value ? Number(e.target.value) : '');
                setPage(1);
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{t('logbook.all')}</MenuItem>
              {months.map((month) => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleDateString(i18n.language, { month: 'long' })}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('logbook.flightType')}
              value={flightTypeFilter}
              onChange={(e) => {
                setFlightTypeFilter(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{t('logbook.all')}</MenuItem>
              <MenuItem value="solo">{t('logbook.solo')}</MenuItem>
              <MenuItem value="tandem">{t('logbook.tandem')}</MenuItem>
            </TextField>
          </Box>
        )}

        {/* Flight Cards */}
        {flights.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FlightTakeoff sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('logbook.noFlights')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('logbook.noFlightsDescription')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => navigate('/app/logbook/add')}
            >
              {t('logbook.addFirstFlight')}
            </Button>
          </Box>
        ) : (
          <>
            {flights.map(renderFlightCard)}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                <Button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  {t('logbook.previous')}
                </Button>
                <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                  {page} / {totalPages}
                </Typography>
                <Button
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  {t('logbook.next')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('logbook.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('logbook.deleteConfirmMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('logbook.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('logbook.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LogbookDashboard;
